import {
  QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysReleaseNotes } from '../queryKeysReleaseNotes';

import { fetchReleaseNotes } from './fetchReleaseNotes';
import { ReleaseNotesQueryParams, ReleaseNotesResponse } from './types';
import { extractReleases } from './utils';
import { zodReleaseNotesQueryParamsSchema } from './validations';

/**
 * Generates the query options for the release notes query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the release notes.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getReleaseNotesQueryOptions = ({
  queryParams,
  options,
}: {
  queryParams?: Partial<ReleaseNotesQueryParams>;
  options?: Omit<
    UseQueryOptions<ReleaseNotesResponse, Error, ReleaseNotesResponse>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const validatedQueryParams = zodReleaseNotesQueryParamsSchema.parse(
    queryParams ?? {},
  );

  return queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchReleaseNotes(validatedQueryParams, { signal }),
    queryKey: queryKeysReleaseNotes.get(validatedQueryParams),
    refetchInterval: ms('60s'),
    select: (data) =>
      extractReleases({
        markdown: data,
        version: validatedQueryParams.version,
        comparator: validatedQueryParams.comparator,
      }),
    staleTime: ms('60s'),
    ...options,
  });
};

/**
 * React Query based custom hook for getting the release notes data with given query parameters.
 *
 * @param queryParams - The query parameters for fetching the release notes.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 */
export const useQueryReleaseNotes = (
  queryParams?: Partial<ReleaseNotesQueryParams>,
  options?: Omit<
    UseQueryOptions<ReleaseNotesResponse, Error, ReleaseNotesResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getReleaseNotesQueryOptions({
      options,
      queryParams,
    }),
  });
